'use client';

import { useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react';
import { Button } from './button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: React.ReactNode;
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Optional icon rendered inline-left of the title (e.g. variant cue). */
  headerIcon?: React.ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  showCloseButton = true,
  size = 'md',
  headerIcon,
}: ModalProps) {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open && showCloseButton) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={`fixed left-[50%] top-[50%] z-50 w-full ${sizeClasses[size]} mx-4 translate-x-[-50%] translate-y-[-50%] glass-strong rounded-lg shadow-2xl border data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 duration-200 outline-none`}
          onEscapeKeyDown={showCloseButton ? undefined : (e) => e.preventDefault()}
          onPointerDownOutside={showCloseButton ? () => onClose() : (e) => e.preventDefault()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b glass-subtle">
            <div className="flex-1 flex items-start gap-3 min-w-0">
              {headerIcon && (
                <span className="shrink-0 flex items-center h-7" aria-hidden="true">
                  {headerIcon}
                </span>
              )}
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-xl font-semibold leading-7">{title}</DialogPrimitive.Title>
                {description && (
                  <DialogPrimitive.Description className="text-sm text-muted-foreground mt-1">{description}</DialogPrimitive.Description>
                )}
              </div>
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="ml-4 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Content */}
          {children && (
            <div className="p-6">
              {children}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
}: ConfirmModalProps) {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const isDestructive = variant === 'destructive';
  const VariantIcon = isDestructive ? AlertTriangle : Info;
  const iconColour = isDestructive
    ? 'text-red-600 dark:text-red-400'
    : 'text-blue-600 dark:text-blue-400';

  // Variant icon sits in the header alongside the title — matches AlertModal
  // and keeps alignment stable regardless of body content length.
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      headerIcon={<VariantIcon className={`h-6 w-6 ${iconColour}`} aria-hidden="true" />}
    >
      <p className="text-sm text-muted-foreground">{description}</p>
      <div className="flex gap-3 justify-end pt-4">
        <Button variant="outline" onClick={onClose}>
          {cancelText}
        </Button>
        <Button
          variant={isDestructive ? 'destructive' : 'default'}
          onClick={handleConfirm}
        >
          {confirmText}
        </Button>
      </div>
    </Modal>
  );
}

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Optional supporting copy. When omitted, the title + header icon stand alone. */
  message?: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
}

export function AlertModal({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
}: AlertModalProps) {
  // Auto-dismiss success alerts after 2 seconds
  useEffect(() => {
    if (isOpen && variant === 'success') {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, variant, onClose]);

  const variantConfig = {
    success: {
      icon: CheckCircle2,
      textColour: 'text-green-600 dark:text-green-400',
    },
    error: {
      icon: XCircle,
      textColour: 'text-red-600 dark:text-red-400',
    },
    warning: {
      icon: AlertTriangle,
      textColour: 'text-yellow-600 dark:text-yellow-400',
    },
    info: {
      icon: Info,
      textColour: 'text-blue-600 dark:text-blue-400',
    },
  };

  const { icon: VariantIcon, textColour } = variantConfig[variant];

  // The variant cue sits beside the title in the header so it stays aligned
  // even when the caller omits `message`. The body then only renders a copy
  // row when there is actually something to say.
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      headerIcon={<VariantIcon className={`h-6 w-6 ${textColour}`} aria-hidden="true" />}
    >
      {message && (
        <p className="text-sm text-muted-foreground">{message}</p>
      )}
      <div className="flex justify-end pt-4">
        <Button onClick={onClose}>
          OK
        </Button>
      </div>
    </Modal>
  );
}

